import { isEmpty } from "lodash-es";
import debounce from "lodash-es/debounce";
import { motion, AnimatePresence } from "motion/react";
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAddSearchHistory } from "~/api/mutations/useAddSearchHistory";
import { useSubjects } from "~/api/queries";
import { useSearchHistory, useSearchLessonQueryOptions } from "~/api/queries/useOpensearch";
import { useSchoolClasses } from "~/api/queries/useSchoolClasses";
import searchImage from "~/assets/images/search.png";
import { Icon } from "~/components/Icon";
import { Button } from "~/components/ui/button";
import { Dialog, DialogOverlay, DialogContent } from "~/components/ui/dialog";
import { MultiSelect } from "~/components/ui/multi-select-v2";
import { Separator } from "~/components/ui/separator";
import { cn } from "~/lib/utils";
import { SearchResult } from "~/modules/Client/Courses/SearchLesson/components/SearchResult";
import { getBadgeVariant } from "~/utils/getBadgeVariant";
import { sortSubjects } from "~/utils/sortSubjects";

import { SearchResults } from "./components/SearchResults";

type FilterConfig = {
  name: string;
  placeholder: string;
  label: string;
  options:
    | {
        value: string;
        label: string;
      }[]
    | undefined;
};

type SearchLessonDialogProps = {
  open: boolean;
  onClose: () => void;
};

export default function SearchLessonDialog({ open, onClose }: SearchLessonDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();

  const [inputValue, setInputValue] = useState("");
  const [phrase, setPhrase] = useState("");
  const debouncedSetPhrase = useRef(
    debounce((value: string) => {
      if (value.length >= 3) {
        setPhrase(value);
        window.dataLayer.push({
          event: "search_lesson",
          search_term: value,
          search_category: "lesson",
        });
      } else {
        setPhrase("");
      }
    }, 400),
  ).current;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    debouncedSetPhrase(e.target.value);
  };

  type FilterKeys = "schoolClasses" | "subjects";
  const [filters, setFilters] = useState<Record<FilterKeys, string[]>>({
    schoolClasses: [],
    subjects: [],
  });

  const handleFilterChange = (name: keyof typeof filters, valueArray: string[]) => {
    setFilters((prev) => ({ ...prev, [name]: valueArray }));
  };

  const handleOnClose = (lessonId: string) => {
    addSearchHistory({
      phrase,
      lessonId,
    });
    setPhrase("");
    onClose();
  };

  const { data: searchLessonData, isLoading } = useSearchLessonQueryOptions({
    index: "lessons",
    phrase,
    schoolClasses: filters.schoolClasses,
    subjects: filters.subjects,
  });

  const { data: subjects } = useSubjects();
  const { data: schoolClasses } = useSchoolClasses();
  const { data: searchHistory } = useSearchHistory("lessons", { enabled: open });
  const { mutate: addSearchHistory } = useAddSearchHistory();

  const filterConfig: FilterConfig[] = [
    {
      name: "schoolClasses",
      placeholder: t("common.other.all_items"),
      label: t("searchLessonView.filters.placeholder.schoolClass"),
      options: schoolClasses?.map(({ name, id }) => ({
        value: id,
        label: name,
      })),
    },
    {
      name: "subjects",
      placeholder: t("common.other.all_items"),
      label: t("searchLessonView.filters.placeholder.subject"),
      options: sortSubjects(subjects).map(({ title, id }) => ({
        value: id,
        label: title,
      })),
    },
  ];

  const inputRef = useRef<HTMLInputElement>(null);
  const isActive = !!phrase.length;
  const [isFocused, setIsFocused] = useState(false);

  const handleClearPhrase = async () => {
    setPhrase("");
    setInputValue("");
  };

  const handleClearFormFilters = () => {
    setFilters({
      schoolClasses: [],
      subjects: [],
    });
  };

  const isAnyFilterApplied = !isEmpty(filters.schoolClasses) || !isEmpty(filters.subjects);

  const dialogRef = useRef<HTMLDivElement>(null);

  const resultsCount = searchLessonData?.data?.length ?? 0;

  const isResults = searchLessonData?.data && phrase.length >= 3 && !isLoading;

  const prevHeight = useRef<HTMLDivElement>(null);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogOverlay className="fixed inset-0 bg-[#7016EB47] backdrop-blur-[8px]" />

      <DialogContent
        ref={dialogRef}
        aria-labelledby={titleId}
        className="z-[150] flex h-max max-h-[100dvh] min-h-[244px] w-full max-w-none flex-col overflow-visible !rounded-3xl px-3 py-4 transition-all duration-1000 sm:w-11/12 sm:max-w-[60rem] sm:px-9 sm:py-10 md:max-h-[75dvh] [@media(max-height:500px)]:overflow-visible"
      >
        <div className="flex items-center gap-3">
          <img src={searchImage} alt="" className="w-9" />
          <h2 id={titleId} className="font-madimi text-2xl font-medium">
            {t("searchLesson.title")}
          </h2>
        </div>
        <div className="flex w-full items-center gap-2">
          <label
            htmlFor="search"
            key="phrase"
            className={cn(
              "flex w-full items-center gap-x-2 rounded-xl border border-neutral-200 px-3 py-2.5 ring-4 ring-violet-400 hover:border-black hover:bg-violet-50 focus:border-violet-950 focus:bg-violet-100 focus:text-violet-950 focus:ring-4 focus:ring-violet-400",
              {
                "border-violet-950 bg-violet-100": phrase,
              },
            )}
          >
            <Icon
              name="MAGNIFYINGGLASS"
              className={cn("size-4 transition-colors", {
                "text-violet-950": isActive || isFocused,
                "text-black": !isActive && !isFocused,
              })}
            />
            <div
              className={cn("h-4 w-px transition-colors", {
                "bg-neutral-800": isActive || isFocused,
                "bg-neutral-300": !isActive && !isFocused,
              })}
            />
            <input
              id="search"
              ref={inputRef}
              type="text"
              aria-label={t("searchLesson.title")}
              placeholder={t("searchLesson.filters.placeholder.searchByKeyword")}
              className={cn(
                "w-full border-none bg-transparent text-violet-950 caret-violet-950 outline-none ring-0 placeholder:text-neutral-800",
              )}
              value={inputValue}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onChange={handleInputChange}
              defaultValue={undefined}
            />
            <button
              className={cn({
                invisible: !phrase || !inputValue,
              })}
              onClick={handleClearPhrase}
              aria-label={t("common.button.clearAll")}
            >
              <Icon name="CROSS" className="size-4 text-[#141B34]" />
            </button>
          </label>
        </div>

        <div className="flex flex-col items-center gap-2 sm:flex-row sm:flex-wrap md:flex-nowrap">
          {filterConfig.map((filter) => {
            const values = filters[filter.name as FilterKeys];
            const options = filter.options ?? [];

            return (
              <div className="w-full *:w-full md:w-max md:max-w-xl md:*:w-max" key={filter.name}>
                <MultiSelect
                  options={options}
                  onValueChange={(value) => handleFilterChange(filter.name as FilterKeys, value)}
                  value={values}
                  placeholder={filter.placeholder}
                  label={filter.label}
                  maxCount={1}
                  container={dialogRef.current}
                />
              </div>
            );
          })}
          <div className="flex justify-center self-end sm:block">
            {isAnyFilterApplied && (
              <Button
                variant="link"
                onClick={handleClearFormFilters}
                aria-label={t("common.button.clearAll")}
              >
                {t("searchLesson.filters.button.reset")}
              </Button>
            )}
          </div>
          {isResults && (
            <span className="ml-auto shrink-0">
              {t("searchLesson.filters.results", { count: resultsCount })}
            </span>
          )}
        </div>
        <Separator className="my-1" />
        <AnimatePresence mode="wait">
          <motion.div
            key={`${phrase}-${resultsCount}-${searchHistory?.length}`}
            style={{ overflow: "auto" }}
            initial={{ height: prevHeight.current?.offsetHeight ?? 0 }}
            animate={!isLoading ? { height: "auto" } : undefined}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            ref={prevHeight}
          >
            <div
              className={cn("grid shrink-0 grid-cols-1 gap-2 duration-700 md:grid-cols-2", {
                "overflow-auto": isResults,
              })}
            >
              {phrase.length >= 3 && isLoading ? (
                <div
                  className={cn(
                    "col-span-2 mx-auto flex flex-col items-center gap-1",
                    !!searchHistory?.length && "mb-4",
                  )}
                >
                  <div className="flex space-x-4 pb-4 pt-6">
                    <Icon name="ELIPSE" className="animate-big-bounce size-2.5 text-[#4E11A1]" />
                    <Icon
                      name="ELIPSE"
                      className="animate-big-bounce size-2.5 text-[#5D12C4] !delay-100"
                    />
                    <Icon
                      name="ELIPSE"
                      className="animate-big-bounce size-2.5 text-[#8B4DFF] !delay-200"
                    />
                    <Icon
                      name="ELIPSE"
                      className="animate-big-bounce size-2.5 text-[#C2AEFF] !delay-300"
                    />
                  </div>
                  <span>{t("searchLessonView.loading")}</span>
                </div>
              ) : (
                <SearchResults
                  isLoading={isLoading}
                  handleCloseModal={handleOnClose}
                  phrase={phrase}
                  results={searchLessonData?.data}
                  getBadgeVariant={getBadgeVariant}
                />
              )}
            </div>
            {!!searchHistory?.length && (
              <div
                className={cn("flex flex-col gap-y-4", {
                  "mt-4 lg:mt-8": isResults,
                  "mt-0": !isResults,
                })}
              >
                <p className="h7 text-neutral-950">{t("searchLesson.lastLessonsTitle")}</p>
                <div
                  className={cn("grid h-full grid-cols-1 gap-2 md:grid-cols-2", {
                    "overflow-auto": searchHistory?.length,
                  })}
                >
                  {searchHistory?.map((result) => (
                    <SearchResult
                      key={result.id}
                      result={result}
                      getBadgeVariant={getBadgeVariant}
                      handleCloseModal={handleOnClose}
                    />
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
